import {
  CursorType,
  DiagramActionType,
  DiagramModeType,
  DiagramZoomType,
  DiagramType,
} from '@/enums/diagrams';
import {CursorManager} from './cursors/cursorManager';
import {remove} from 'lodash';
import {CursorData, CursorDiagramState} from './cursorDiagramState';
import {ActionManager} from './diagramActions/actionManager';
import {DiagramAction} from './diagramActions/diagramAction';
import {DiagramMode} from '@/interfaces/diagrams';
import {SelectedChipsData} from '@/components/controls/interfaces';
import {CursorLabelModel} from './cursors/labelModels/cursorLabelModel';
import {MarkerCursorLabelModel} from './cursors/labelModels/markerCursorLabelModel';
import {union} from 'lodash';

/*
Менеджер режимов графика
*/

export class CursorDiagramModeManager {
  //Тип зума
  zoomType = DiagramZoomType.None;

  //Границы зума по X
  extremesX: any[] = [];

  //Границы зума по Y
  extremesY: any[] = [];

  //Показатель применения зума по Y
  isInYZoom = false;

  changedZoomXKey = 0;

  // Текущее состояние диаграммы
  private state: CursorDiagramState;

  //Менеджер курсоров
  private cursorManager: CursorManager;

  //Менеджер действий
  private actionManager: ActionManager;

  //Диаграмма
  private chart: any;

  private xMinScaleValue!: number;

  constructor(
    diagramType: DiagramType | null = null,
    simpleLabelModel: CursorLabelModel | null = null,
    markerLabelModel: MarkerCursorLabelModel | null = null,
    applyCallback: Function | null = null,
    actions: DiagramAction[],
    state: CursorDiagramState,
  ) {
    this.cursorManager = new CursorManager(
      diagramType,
      simpleLabelModel,
      markerLabelModel,
      applyCallback,
    );
    this.actionManager = new ActionManager(actions);
    this.state = state;
  }

  // Инициализация менеджера
  init(
    chart: any,
    bindedSeriesIds: string[],
    xMinScaleValue: number,
    setExtremesXCallback: Function | null = null,
  ) {
    this.cursorManager.init(chart, bindedSeriesIds);
    this.actionManager.init(chart, bindedSeriesIds);
    this.setInitState(this.state);

    this.chart = chart;
    this.xMinScaleValue = xMinScaleValue;

    if (setExtremesXCallback) this.extremesX = setExtremesXCallback();
    else this.extremesX = [chart.xAxis[0].getExtremes()];

    this.extremesY = [chart.yAxis[0].getExtremes()];
  }

  // Применить зум
  applyZoom(xAxis: any, yAxis: any, callback: Function | null = null) {
    if (callback)
      return callback(xAxis, yAxis, this.chart, this.extremesX, this.extremesY, this.state);

    // Применяем зум по Y
    if (yAxis) {
      this.chart.yAxis[0].setExtremes(yAxis.min, yAxis.max);
      this.extremesY.push({min: yAxis.min, max: yAxis.max});
      this.isInYZoom = true;
    }

    // Применяем зум по X
    if (xAxis) {
      const startDateTime = Math.round(xAxis.min);
      const endDateTime = Math.round(xAxis.max);
      if (Math.abs(startDateTime - endDateTime) > this.xMinScaleValue) {
        this.extremesX.push({min: xAxis.min, max: xAxis.max});
        this.state.selectedZoomXData = {active: true, interval: {startDateTime, endDateTime}};
        this.changedZoomXKey++;
        return {startDateTime, endDateTime};
      }
    }
    return null;
  }

  // Сбросить зум
  skipZoom(updateState = true) {
    if (!this.hasViewMode()) {
      this.state.selectedZoomXData = {active: false};
      if (updateState) {
        this.changedZoomXKey++;
        return;
      }
      return true;
    }

    this.clearZoom();

    const baseExtremesX = this.extremesX[0];
    const baseExtremesY = this.extremesY[0];

    //Сбрасываем зум по Y
    const currentY = this.chart.yAxis[0].getExtremes();

    if (currentY.min !== baseExtremesY.min || currentY.max !== baseExtremesY.max)
      this.chart.yAxis[0].setExtremes(null, null);

    //Сбрасываем зум по X
    const currentX = this.chart.xAxis[0].getExtremes();

    if (updateState) {
      this.state.selectedZoomXData = {active: false};
      this.changedZoomXKey++;
    }
    if (currentX.min !== baseExtremesX.min || currentX.max !== baseExtremesX.max) return true;
    return false;
  }

  // Отменить зум
  undoZoom(callback: Function | null = null) {
    if (callback) return callback(this.chart, this.extremesX, this.extremesY, this.state);

    if (this.extremesX.length > 1) this.extremesX.pop();
    if (this.extremesY.length > 1) this.extremesY.pop();

    const prevExtremesY = this.extremesY[this.extremesY.length - 1];
    const currentY = this.chart.yAxis[0].getExtremes();
    if (currentY.min !== prevExtremesY.min || currentY.max !== prevExtremesY.max)
      this.chart.yAxis[0].setExtremes(prevExtremesY.min, prevExtremesY.max);

    if (this.extremesY.length === 1) this.isInYZoom = false;

    const currentX = this.chart.xAxis[0].getExtremes();
    const prevExtremesX = this.extremesX[this.extremesX.length - 1];
    if (this.extremesX.length > 1) {
      if (currentX.min !== prevExtremesX.min || currentX.max !== prevExtremesX.max) {
        const startDateTime = Math.round(prevExtremesX.min);
        const endDateTime = Math.round(prevExtremesX.max);
        this.state.selectedZoomXData = {active: true, interval: {startDateTime, endDateTime}};
        this.changedZoomXKey++;
        return {startDateTime, endDateTime};
      }
    } else if (currentX.min !== prevExtremesX.min || currentX.max !== prevExtremesX.max) {
      this.state.selectedZoomXData = {active: false};
      this.changedZoomXKey++;
      return 'skip';
    }
    return null;
  }

  //Очистить зум
  clearZoom() {
    this.extremesX.length = 1;
    this.extremesY.length = 1;
    this.isInYZoom = false;
    this.state.selectedZoomXData = {active: false};
  }

  // Установить состояние
  setInitState(state: CursorDiagramState, isSync = false) {
    this.selectModes(state.activeModes, state.cursorData, state.actionData, isSync);
    const activeModes = [...this.state.activeModes];
    this.state = new CursorDiagramState(state);
    this.state.activeModes = [...activeModes];
    this.state.cursorData = this.cursorManager.getActiveCursorsData();
    this.state.actionData = this.actionManager.getAppliedActionsData();
  }

  patchCursorState(state: CursorDiagramState, isSync = false) {
    //Не курсоры, которые были в старом состоянии + курсоры из нового состояния
    this.selectModes(
      union(
        this.state.activeModes.filter(x => x.type !== DiagramModeType.Cursor),
        state.activeModes.filter(x => x.type == DiagramModeType.Cursor),
      ),
      state.cursorData,
      state.actionData,
      isSync,
    );
    const activeModes = [...this.state.activeModes];

    const newState = new CursorDiagramState();
    newState.activeModes = [...activeModes];
    newState.isShowLine = state.isShowLine;
    newState.isShowTooltips = state.isShowTooltips;
    newState.cursorData = [...state.cursorData];
    newState.actionData = new Map(state.actionData);
    newState.diagramId = state.diagramId;
    newState.isUpdateDiagram = state.isUpdateDiagram;

    //интервалы из старого состояния
    newState.selectedChipsData = this.state.selectedChipsData;

    //зум з старого состояния
    newState.selectedZoomXData = this.state.selectedZoomXData;

    this.state = newState;

    this.state.cursorData = this.cursorManager.getActiveCursorsData();
    this.state.actionData = this.actionManager.getAppliedActionsData();
  }

  patchIntervalState(state: CursorDiagramState) {
    this.state.diagramId = state.diagramId;
    this.state.selectedChipsData = state.selectedChipsData;
  }

  patchZoomState(state: CursorDiagramState, isSync = false) {
    this.selectModes(
      union(
        this.state.activeModes.filter(x => x.type !== DiagramModeType.View),
        state.activeModes.filter(x => x.type == DiagramModeType.View),
      ),
      this.state.cursorData,
      this.state.actionData,
      isSync,
    );
    const activeModes = [...this.state.activeModes];

    this.state.activeModes = activeModes;
    this.state.diagramId = state.diagramId;
    this.state.selectedZoomXData = state.selectedZoomXData;
  }

  updateState(modes: DiagramMode[]) {
    this.state.cursorData = this.cursorManager.getActiveCursorsData();
    this.state.actionData = this.actionManager.getAppliedActionsData();
    this.selectModes(modes, this.state.cursorData, this.state.actionData);
  }

  //Возвращает установлен ли режим просмотра
  hasViewMode() {
    return this.state.activeModes.some(mode => mode.type == DiagramModeType.View);
  }

  //Возвращает показатель допустимости "живых" обновлений графика
  canLiveUpdate() {
    return (
      !this.state.activeModes.some((mode: DiagramMode) => !mode.canLiveUpdate) &&
      this.state.isUpdateDiagram
    );
  }

  //Перерисовка активных режимов
  updateActiveModes(state: CursorDiagramState, bindedSeriesIds: string[]) {
    this.cursorManager.updateActiveCursors(state.cursorData, bindedSeriesIds);
    this.actionManager.updateAppliedActions(state.actionData, bindedSeriesIds);
  }

  renderActiveModes() {
    this.cursorManager.renderActiveCursors();
    this.actionManager.renderAppliedActions();
  }

  //Показать линию
  showLine(show: boolean) {
    this.cursorManager.showLine(show);
    this.state.isShowLine = show;
  }

  //Показать отметки
  showTooltips(show: boolean) {
    this.cursorManager.showTooltips(show);
    this.state.isShowTooltips = show;
  }

  //Включить режим обновления графика
  updateDiagram(state: boolean) {
    this.state.isUpdateDiagram = state;
  }

  //Установить данные чипсов
  setSelectedChipsData(value: SelectedChipsData | null) {
    this.state.selectedChipsData = value;
  }

  //Запросить состояние режимов диаграммы
  getState() {
    this.state.cursorData = this.cursorManager.getActiveCursorsData();
    this.state.actionData = this.actionManager.getAppliedActionsData();
    return new CursorDiagramState(this.state);
  }

  //Выбрать режимы
  private selectModes(
    modes: DiagramMode[],
    cursorData: CursorData[],
    actionData: Map<DiagramActionType, any>,
    isSync = false,
  ) {
    //смотрим какие режимы нужно сбросить, а какие применить
    //сбросить - есть в activeModes и нет в modes
    const skipModes = this.state.activeModes.filter(
      mode => (!isSync || mode.isSync) && !modes.some(x => x.id === mode.id),
    );

    //обновить - есть и в activeModes и в modes
    const updateModes = this.state.activeModes.filter(
      mode => (!isSync || mode.isSync) && modes.some(x => x.id === mode.id),
    );

    //применить - есть в modes и нет в activeModes
    const applyModes = modes
      .filter(x => !isSync || x.isSync)
      .filter(mode => !this.state.activeModes.some(x => x.id === mode.id));

    //удаляем состояния, которые должны сброситься
    this.state.activeModes.forEach(mode => {
      if (applyModes.some(x => !x.compatibleModeIds.includes(mode.id)))
        if (!skipModes.some(x => x.id === mode.id)) skipModes.push(mode);
    });

    skipModes?.forEach(mode => {
      this.skipMode(mode);
    });

    applyModes.forEach(mode => {
      this.applyMode(mode, cursorData, actionData);
    });

    // существующие режимы обновляются после применения новых
    updateModes?.forEach(mode => {
      this.updateMode(mode, cursorData, actionData);
    });
  }

  // Обновить режим
  private updateMode(
    mode: DiagramMode,
    cursorData: CursorData[],
    actionData: Map<DiagramActionType, any>,
  ) {
    if (mode.type == DiagramModeType.Cursor) {
      const cursorType = mode.id as CursorType;
      /*      cursorData = cursorData.filter(
        cd =>
          cd.type == cursorType ||
          (cursorType == CursorType.Cursor && cd.type == CursorType.HarmonicCursor),
      );*/
      if (cursorData.length < this.cursorManager.cursors.length) {
        for (let i = this.cursorManager.cursors.length - 1; i >= 0; i--) {
          if (
            !cursorData.some(
              cd =>
                cd.pointX?.toFixed(3) ==
                this.cursorManager.cursors[i].getCursorData().pointX?.toFixed(3),
            )
          ) {
            this.cursorManager.removeCursor(this.cursorManager.cursors[i]);
          }
        }
      } else if (cursorData.length > 0) {
        cursorData.forEach((cd, index) => this.cursorManager.updateCursorIdx(index, cd));
      } else {
        this.cursorManager.updateCursor(cursorType, null);
      }
    }

    if (mode.type == DiagramModeType.View) {
      this.cursorManager.lastActiveCursor?.freezeEvents(true);
      this.zoomType = DiagramZoomType.X;
    }

    if (mode.type == DiagramModeType.Action) {
      const actionType = mode.id as DiagramActionType;
      this.actionManager.updateAction(actionType, actionData.get(actionType));
    }
  }

  // Сбросить режим
  private skipMode(mode: DiagramMode) {
    // TODO может быть вынести обработчики режимов
    if (mode.type == DiagramModeType.Cursor) this.cursorManager.clearCursor(mode.id as CursorType);

    if (mode.type == DiagramModeType.View) {
      //разблокировка событий курсоров
      this.cursorManager.lastActiveCursor?.freezeEvents(false);
      this.zoomType = DiagramZoomType.None;
    }

    if (mode.type == DiagramModeType.Action)
      this.actionManager.cancelAction(mode.id as DiagramActionType);

    remove(this.state.activeModes, activeMode => activeMode.id == mode.id);
  }

  //Применить режим
  private applyMode(
    mode: DiagramMode,
    cursorData: CursorData[],
    actionData: Map<DiagramActionType, any>,
  ) {
    // TODO может быть вынести обработчики режимов
    if (mode.type == DiagramModeType.Cursor) {
      const cursorType = mode.id as CursorType;
      cursorData = cursorData.filter(
        cd =>
          cd.type == cursorType ||
          (cursorType == CursorType.Cursor && cd.type == CursorType.HarmonicCursor),
      );
      if (cursorData.length > 0)
        cursorData.forEach(cd => this.cursorManager.applyCursor(cursorType, cd));
      else this.cursorManager.applyCursor(cursorType, null);
    }

    if (mode.type == DiagramModeType.View) {
      this.cursorManager.lastActiveCursor?.freezeEvents(true);
      this.zoomType = mode.value;
    }

    if (mode.type == DiagramModeType.Action) {
      const actionType = mode.id as DiagramActionType;
      let data = actionData.get(actionType);
      if (!data) data = mode.value;
      this.actionManager.applyAction(actionType, data);
    }

    this.state.activeModes.push(mode);
  }

  getCursorManager() {
    return this.cursorManager;
  }
}
